import { withErrorBoundary } from "@sentry/nextjs";
import { gql } from "graphql-request";
import { FC } from "react";
import styled from "styled-components";
import { Markdown } from "components";
import { Introduction1CardContent } from "types/graphql/generated";
import { IndividualCard } from "./IndividualCard";
import { BusinessCard } from ".";

interface Props {
  className?: string;
  content: Introduction1CardContent;
}

const Card: FC<Props> = withErrorBoundary(
  ({
    className = "",
    content: {
      title: Introduction1CardTitle,
      accountSetup,
      subTitle,
      compare,
      introduction1Business,
      introduction1Individual,
    },
  }) => {
    return (
      <StyledCard className={className}>
        <div className="Card__topCard">
          <div className="Card__titleContainer">
            <h2 className="Color_primary Font_48_54_600 md:Font_40_48_600">{Introduction1CardTitle}</h2>
            <Markdown className="Card__subTitle Color_secondary Font_24_32_450 ">{subTitle}</Markdown>
          </div>
          <div className="Card__topCardContent">
            <BusinessCard content={introduction1Business} />
            <Markdown className="Card__compareLinkMobile Color_secondary Font_18_26_450 sm:Font_16_22_450">
              {compare}
            </Markdown>
            <IndividualCard className="Card__individual" content={introduction1Individual} />
          </div>
        </div>
        <div className="Card__bottomCard Color_secondary lg:FullBleed">
          <Markdown className="Card__compareLink Font_18_26_450">{compare}</Markdown>
          <Markdown className="Card__bottomCardAccountSetup Font_18_26_450 sm:Font_16_22_450">{accountSetup}</Markdown>
        </div>
      </StyledCard>
    );
  },
  {}
);

const StyledCard = styled.div`
  .Card__topCard {
    padding: 72px 56px;
    background-color: ${({ theme }) => theme.colors.fog["00"]};
  }

  .Card__titleContainer {
    .Card__subTitle {
      max-width: 822px;
      margin-top: 16px;
    }
  }

  .Card__topCardContent {
    display: flex;
    justify-content: space-between;
    margin-top: 80px;
  }

  .Card__compareLinkMobile {
    display: none;
  }

  .Card__individual {
    margin-left: 40px;
  }

  .Card__bottomCard {
    display: flex;
    align-items: center;
    padding: 43px 64px;
    border-top: 1px solid ${({ theme }) => theme.colors.fog["30"]};
    background-color: ${({ theme }) => theme.colors.fog["10"]};
  }

  .Card__bottomCardAccountSetup {
    margin-left: 40px;
    padding-left: 40px;
    border-left: 1px solid ${({ theme }) => theme.colors.fog["30"]};
  }

  @media (max-width: 1090px) {
    .Card__topCard {
      padding: 64px 56px;
    }

    .Card__topCardContent {
      margin-top: 56px;
    }

    .Card__bottomCard {
      padding: 32px 64px;
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.lg}px`}) {
    .Card__topCard {
      padding: 64px 0 24px;
    }

    .Card__topCardContent {
      flex-direction: column;
    }

    .Card__individual {
      margin-top: 40px;
      margin-left: unset;
    }

    .Card__optionDescription {
      max-width: unset;
    }

    .Card__bottomCardAccountSetup {
      max-width: 353px;
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.md}px`}) {
    padding-bottom: 64px;
    border-bottom: 1px solid ${({ theme }) => theme.colors.fog["30"]};

    .Card__compareLinkMobile {
      margin-top: 24px;
      display: flex;
    }

    .Card__individual {
      margin-top: 56px;
    }

    .Card__bottomCard {
      width: unset;
      margin-left: unset;
      padding: 16px 24px;
      border-radius: 0;
      border-top: none;
    }

    .Card__compareLink {
      display: none;
    }

    .Card__bottomCardAccountSetup {
      max-width: unset;
      margin-left: 0;
      padding-left: 0;
      border-left: none;
    }
  }
`;

gql`
  fragment Introduction1CardContent on ComponentNestedIntroduction1Card {
    title
    subTitle
    accountSetup
    compare
    introduction1Business {
      ...Introduction1BusinessCardContent
    }
    introduction1Individual {
      ...Introduction1IndividualCardContent
    }
  }
`;

export { Card };
